import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import type { Lang } from '@/models/lang';
import { WEEKDAY_LIST } from '@/wabunka-src/constants/calendar';

dayjs.locale('ja');
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekOfYear);
dayjs.tz.setDefault('Asia/Tokyo');

const JST_OFFSET = 540;

export type HyphenatedDate = `${string}-${string}-${string}`;

export const getCurrentJstDayjs = (): dayjs.Dayjs => {
  return dayjs().tz();
};

export const getCurrentHyphenatedDate = (): HyphenatedDate => {
  const now = getCurrentJstDayjs();
  return `${now.format('YYYY')}-${now.format('MM')}-${now.format('DD')}`;
};

export const getYearFromHyphenatedDate = (hyphenatedDate: HyphenatedDate): string => {
  return hyphenatedDate.split('-')[0];
};

export const getMonthFromHyphenatedDate = (hyphenatedDate: HyphenatedDate): string => {
  return hyphenatedDate.split('-')[1];
};

/**
 * ローカルタイムゾーンとJSTとの時差を取得する
 * 例：ローカルタイムゾーンがUTC+13:00の場合
 *   JST(UTC+09:00)との時差の+240（+4時間）を返却する
 * @returns ローカルタイムゾーンとJSTとの時差
 */
export const getTimeDifferenceFromJst = (localTimeDayjs: dayjs.Dayjs): number => {
  return -(localTimeDayjs.toDate().getTimezoneOffset() + JST_OFFSET);
};

/**
 * 日時形式の文字列を時差を反映したJSTのDayjsオブジェクトに変換する
 *
 * 文字列をDayjsオブジェクトに変換する際に、ローカルタイムゾーンとJSTとの時差が考慮された日時に変換される
 * 例：ローカルタイムゾーンがUTC+13:00で"2023/10/20 21:30:00"を受け取った場合
 *   Dayjs変換時は"2023/10/20 17:30:00"となる
 *   "2023/10/20 21:30:00"に時間を補正するために、ローカルタイムゾーンとJSTとの時差を反映させる
 * @param dateStr
 * @returns JSTのDayjsオブジェクト
 */
export const convertJstDayjsFromString = (dateStr: string | undefined): dayjs.Dayjs => {
  const localTimeDayjs = dayjs(dateStr).tz();
  return localTimeDayjs.add(getTimeDifferenceFromJst(localTimeDayjs), 'minute');
};

/**
 * Nuxt の route から取得できるクエリの型から、カレンダー用の Dayjs へ変換して返却する
 */
export const initExperienceCalendarDayjsFromQuery = (dateQuery: string | (string | null)[]): dayjs.Dayjs | undefined => {
  if (dateQuery && typeof dateQuery === 'string') {
    const dayjsFromQuery = convertJstDayjsFromString(dateQuery);
    if (dayjsFromQuery.isValid()) {
      return dayjsFromQuery;
    }
  }
  return undefined;
};

export const convertJstDayjsFromDate = (date: Date | undefined): dayjs.Dayjs => {
  return dayjs(date).tz();
};

export const convertFormattedJstDateFromDate = (date: Date, format: string): string => {
  return convertJstDayjsFromDate(date).format(format);
};

export const convertFormattedJstDateFromString = (dateStr: string, format: string): string => {
  return convertJstDayjsFromString(dateStr).format(format);
};

/**
 *
 * @param day Dayjs object
 * @returns string in the form of YYYY-MM-DD
 */
export const formatHyphenatedDate = (day: dayjs.Dayjs): string => {
  return day.tz().format('YYYY-MM-DD');
};

/**
 * カレンダーの暦日(Y-M-D)演算を「UTCモードの Dayjs」で行うためのヘルパー。
 *
 * dayjs の timezone プラグイン（`.tz('Asia/Tokyo')` で得たオブジェクト）に対する
 * `startOf` / `add` 等の演算は、実行環境（ブラウザ）のローカルタイムゾーンが
 * 夏時間(DST)の遷移日付近の日付を扱うと内部でオフセットを誤算出し、日付が 1 日
 * ずれる不具合がある（例: 北米では 11/1 が DST 終了日のため、11 月の月初演算が
 * ずれてカレンダーから 11 月が消える / 日付が 1 日前にずれる）。
 *
 * JST は DST を持たず常に UTC+9 なので、暦日のみを扱うカレンダー演算は UTC モードで
 * 行えば DST・実行環境TZの影響を一切受けず常に正しい。演算結果はクリーンな instant の
 * ため、表示直前に `formatHyphenatedDate` 等で `.tz()` 整形しても問題ない。
 */
export const getCurrentJstCalendarDate = (): dayjs.Dayjs => {
  return dayjs.utc(getCurrentHyphenatedDate());
};

/**
 * 任意の Dayjs が指す JST の暦日を、UTCモードの Dayjs として返す。
 * カレンダー演算の起点（表示月・選択日など）を DST 非依存に正規化する用途。
 * @see getCurrentJstCalendarDate
 */
export const toJstCalendarDate = (day: dayjs.Dayjs): dayjs.Dayjs => {
  return dayjs.utc(formatHyphenatedDate(day));
};

export const createDefaultDate = (): Date => {
  return new Date('2020-01-01T00:00:00.000Z');
};
export const formatDateToJST = (date: Date): string => {
  const jstTime = convertJstDayjsFromDate(date); // UTCからJSTへ変換
  const formatString = `YYYY年MM月DD日(${WEEKDAY_LIST[jstTime.day()]})HH:mm`; // 曜日を日本語で表示
  return jstTime.format(formatString);
};

export const formatTime = (date: Date, lang?: Lang): string => {
  if (lang === 'en') {
    return dayjs(date).tz().locale('en').format('h:mm A');
  }
  return dayjs(date).tz().format('A HH:mm');
};

export const formatFromDateTimeInEnLocale = (from: Date): string => {
  return dayjs(from).tz().locale('en').format('MMMM D, YYYY h:mm A');
};

export const formatFromToDateTimeInEnLocale = (from: Date, to: Date): string => {
  return `${dayjs(from).tz().locale('en').format('MMMM D, YYYY h:mm A')} - ${dayjs(to).tz().locale('en').format('h:mm A')}`;
};

export const formatUntilDateInEnLocale = (inputDate: Date): string => {
  const date = dayjs(inputDate);
  return date.tz().locale('en').format('h:mm A on MMMM D, YYYY');
};

export const formatYearMonth = (date: dayjs.Dayjs, lang?: Lang): string => {
  if (lang === 'en') {
    return date.tz().locale('en').format('MMMM YYYY');
  }
  return date.tz().format('MMMM YYYY');
};

/**
+ * 日付を英語ロケールでフォーマットする
+ *
+ * @param inputDate フォーマットする日付オブジェクト
+ * @returns 'MMMM D, YYYY' 形式の日付文字列（例: 'January 1, 2023'）
+ */
export const formatDateInEnLocale = (inputDate: Date): string => {
  const date = dayjs(inputDate);
  return date.tz().locale('en').format('MMMM D, YYYY');
};

/**
 * 開始日と終了日から英語の期間文字列を生成する
 *
 * @param start 開始日
 * @param end 終了日
 * @returns 英語の期間文字列
 *   - 同月の場合: "Jan 10 to 20"
 *   - 異なる月の場合: "Jan 10 to Feb 5"
 *   - 異なる年の場合: "Jan 10, 2026 to Feb 5, 2027"
 */
export const formatDurationRangeEn = (start: dayjs.Dayjs, end: dayjs.Dayjs): string => {
  const s = start.tz().locale('en');
  const e = end.tz().locale('en');

  if (s.year() !== e.year()) {
    return `${s.format('MMM D, YYYY')} to ${e.format('MMM D, YYYY')}`;
  }
  if (s.month() === e.month()) {
    return `${s.format('MMM D')} to ${e.format('D')}`;
  }
  return `${s.format('MMM D')} to ${e.format('MMM D')}`;
};
